import { useCallback, useRef, useEffect } from "react";

interface ThrottledTypingConfig {
  throttleInterval?: number; // 节流间隔，默认300ms
  stopDelay?: number; // 停止输入的判断延迟，默认1000ms
}

export const useThrottledTyping = (
  onTyping: (content: string) => void,
  onTypingStop: () => void,
  config: ThrottledTypingConfig = {},
) => {
  const { throttleInterval = 300, stopDelay = 1000 } = config;

  const lastSentRef = useRef<number>(0);
  const stopTimeoutRef = useRef<NodeJS.Timeout>(null);
  const lastContentRef = useRef<string>("");
  const isStoppedRef = useRef<boolean>(true);

  const sendTyping = useCallback(
    (content: string) => {
      const now = Date.now();

      // 节流控制：只有在间隔时间过后才发送
      if (now - lastSentRef.current >= throttleInterval) {
        onTyping(content);
        lastSentRef.current = now;
        lastContentRef.current = content;
      }

      // 清除之前的停止定时器
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
      }

      // 设置新的停止定时器
      stopTimeoutRef.current = setTimeout(() => {
        if (!isStoppedRef.current) {
          onTypingStop();
          isStoppedRef.current = true;
        }
      }, stopDelay);

      isStoppedRef.current = false;
    },
    [onTyping, onTypingStop, throttleInterval, stopDelay],
  );

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
      }
    };
  }, []);

  const forceStop = useCallback(() => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }
    if (!isStoppedRef.current) {
      onTypingStop();
      isStoppedRef.current = true;
    }
  }, [onTypingStop]);

  return { sendTyping, forceStop, lastContent: lastContentRef.current };
};
